import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import { FaPrint } from "react-icons/fa";

import { DataType, EobDataType } from "./claimsMainDataComponent";

type RowDetailsDialogProps = {
  open: boolean;
  onClose: () => void;
  selectedRow: DataType | null;
  eobData: EobDataType[]; // Add this line to hold all related claims
};

export const RowDetailsDialog: React.FC<RowDetailsDialogProps> = ({ open, onClose, selectedRow, eobData }) => {
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");

    const createTableRows = () => {
      return eobData
        .map(
          (claim, index) => `
        <tr key=${index}>
          <td>${claim.Procedure_1 || ""}</td>
          ${claim.Procedure_2 ? `<td>${claim.Procedure_2}</td>` : ""}
          <td>${claim.Service_From || ""}</td>
          <td>${claim.Service_Thru || ""}</td>
          <td>${claim.Billed_Amount || ""}</td>
          <td>${claim.Not_Covered_Amount || ""}</td>
          <td>${claim.Discount_Amount || ""}</td>
          <td>${claim.Applied_to_Ded || ""}</td>
          <td>${claim.Paid_Amount || ""}</td>
          <td>${claim.Deductible_Remaining || ""}</td>
          <td>${claim.Copay_Remaining || ""}</td>
          <td>${claim.Visits_Remaining || ""}</td>
          <td>${claim.Additional_Notes || ""}</td>
        </tr>
      `
        )
        .join("");
    };
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Claim Details</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          .section {
            margin-bottom: 15px;
          }
          h2 {
            font-size: 16px;
            margin-bottom: 5px;
            border-bottom: 1px solid #ccc;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
            font-size: 12px;
          }
          th {
            background-color: #f2f2f2;
          }
          @media print {
            @page {
              size: landscape;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Additional Claims Information</h1>
        </div>
        
        <div class="section">
          <h2>Provider Information</h2>
          <p>Tax ID: ${selectedRow.Tax_ID}</p>
          <p>Provider Name: ${selectedRow.Provider_Name}</p>
        </div>
        
        <div class="section">
          <h2>Provider Address</h2>
          <p>${selectedRow.Provider_Addr1}</p>
          <p>${selectedRow.Provider_City}, ${selectedRow.Provider_State} ${selectedRow.Provider_Zip}</p>
        </div>
        
        <div class="section">
          <h2>EOB Summary for Claim</h2>
          <p>Claim Number: ${selectedRow.Claim_Number}</p>
          <p>Paid Status: ${selectedRow.Status}</p>
          <p>Explanation:  ${
            eobData.find((val) => parseInt(val.Claim_Number) == selectedRow.Claim_Number).Ex_Code_Descriptions === ""
              ? "No Explanation Provided"
              : eobData.find((val) => parseInt(val.Claim_Number) == selectedRow.Claim_Number).Ex_Code_Descriptions
          }</p>
        </div>
        
        <div class="section">
          <h2>EOB Line Items</h2>
          <table>
            <thead>
              <tr>
                <th>Procedure 1</th>
                ${eobData[0] && eobData[0].Procedure_2 ? "<th>Procedure 2</th>" : ""}
                <th>Service From</th>
                <th>Service Thru</th>
                <th>Billed Amount</th>
                <th>Not Covered</th>
                <th>Discount</th>
                <th>Applied to Ded</th>
                <th>Paid Amount</th>
                <th>Deductible Remaining</th>
                <th>Copay Remaining</th>
                <th>Visits Remaining</th>
                <th>Additional Notes</th>
              </tr>
            </thead>
            <tbody>
              ${createTableRows()}
            </tbody>
          </table>
        </div>
        
        <script>
          // Automatically print and then close the window
          window.onload = function() {
            window.print();
            // Uncomment the line below if you want the window to close after printing
            // window.close();
          };
        </script>
      </body>
      </html>
    `;
    // document.write() is deprecated but still works in 90% of cases, really just misusing DOM
    printWindow.document.write(printContent);
    printWindow.document.close();
  };
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="dialog-title" maxWidth="md" fullWidth>
      <DialogTitle id="dialog-title">Additional Claims Information</DialogTitle>
      <DialogContent>
        {selectedRow && (
          <Grid container spacing={2}>
            {/* Provider Information */}
            <Grid item xs={12}>
              <Typography variant="h6">Provider Information</Typography>
              <Divider />
              <Typography variant="body1">
                <strong>Tax ID:</strong> {selectedRow.Tax_ID}
              </Typography>
              <Typography variant="body1">
                <strong>Provider Name:</strong> {selectedRow.Provider_Name}
              </Typography>
              <Typography variant="h6" style={{ marginTop: "16px" }}>
                Provider Address
              </Typography>
              <Divider />
              <Typography variant="body1">{selectedRow.Provider_Addr1}</Typography>
              <Typography variant="body1">{`${selectedRow.Provider_City}, ${selectedRow.Provider_State} ${selectedRow.Provider_Zip}`}</Typography>
            </Grid>

            {/* EOB Summary */}
            <Grid item xs={12}>
              <Typography variant="h6">EOB Summary for Claim:</Typography>
              <Divider />
              <Typography variant="body1">
                <strong>Claim Number:</strong> {selectedRow.Claim_Number}
              </Typography>
              <Typography variant="body1">
                <strong>Paid Status:</strong> {selectedRow.Status}
              </Typography>
              <Typography>
                <strong>Explanation: </strong>
                {eobData.find((val) => parseInt(val.Claim_Number) == selectedRow.Claim_Number).Ex_Code_Descriptions ===
                ""
                  ? "No Explanation Provided"
                  : eobData.find((val) => parseInt(val.Claim_Number) == selectedRow.Claim_Number).Ex_Code_Descriptions}
              </Typography>
            </Grid>

            {/* EOB Line Items */}
            <Grid item xs={12}>
              <Typography variant="h6">EOB Line Items</Typography>
              <Divider />
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Procedure 1</strong>
                      </TableCell>
                      {eobData.find((val) => parseInt(val.Claim_Number) == selectedRow.Claim_Number).Procedure_2 ? (
                        <TableCell
                          sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                        >
                          <strong>Procedure 2</strong>
                        </TableCell>
                      ) : null}
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Service From</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Service Thru</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Total Charges</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Not Covered</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Discount/Penalty</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Applied to Ded</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Benefits Paid</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Deductible Remaining</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Copay Remaining</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Visits Remaining</strong>
                      </TableCell>
                      <TableCell
                        sx={{ backgroundColor: "white", color: "black", whiteSpace: "nowrap", minWidth: "100px" }}
                      >
                        <strong>Additional Notes</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eobData.map((claim, index) => (
                      <TableRow key={index}>
                        <TableCell>{claim.Procedure_1}</TableCell>
                        {claim.Procedure_2 ? <TableCell>{claim.Procedure_2}</TableCell> : null}
                        <TableCell>{claim.Service_From}</TableCell>
                        <TableCell>{claim.Service_Thru}</TableCell>
                        <TableCell>{claim.Billed_Amount}</TableCell>
                        <TableCell>{claim.Not_Covered_Amount}</TableCell>
                        <TableCell>{claim.Discount_Amount}</TableCell>
                        <TableCell>{claim.Applied_to_Ded}</TableCell>
                        <TableCell>{claim.Paid_Amount}</TableCell>
                        <TableCell>{claim.Deductible_Remaining}</TableCell>
                        <TableCell>{claim.Copay_Remaining}</TableCell>
                        <TableCell>{claim.Visits_Remaining}</TableCell>
                        <TableCell>{claim.Additional_Notes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handlePrint} startIcon={<FaPrint />}>
          Print
        </Button>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RowDetailsDialog;
